import { OVERLAY_DEFAULT_CONFIG, OverlayDefaultConfig } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { enableProdMode, importProvidersFrom, provideZonelessChangeDetection } from '@angular/core';
import { MAT_BUTTON_TOGGLE_DEFAULT_OPTIONS } from '@angular/material/button-toggle';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MAT_SLIDE_TOGGLE_DEFAULT_OPTIONS } from '@angular/material/slide-toggle';
import { bootstrapApplication, BrowserModule } from '@angular/platform-browser';
import { provideNoopAnimations } from '@angular/platform-browser/animations';
import { provideRouter } from '@angular/router';
import { ServiceWorkerModule } from '@angular/service-worker';

import { AuthModule } from '@auth0/auth0-angular';
import { NgSelectModule } from '@ng-select/ng-select';
import { NavigationActionTiming, StoreRouterConnectingModule } from '@ngrx/router-store';

import { AppComponent } from './app/app.component';
import { appRoutes } from './app/routes';
import { environment } from './environments/environment';
import { LoggingModule } from './features/logging/logging.module';
import { ConsoleLoggerService } from './features/logging/services/providers/console-logger.service';
import { RaygunLoggerService } from './features/logging/services/providers/raygun-logger.service';
import { ApiRequestInterceptor as SessionSecurityInterceptor } from './features/shared/interceptors/api-request.interceptor';
import { AuthInterceptor } from './features/shared/interceptors/auth.interceptor';
import { ResolutionInterceptor } from './features/shared/interceptors/resolution.interceptor';
import { WebSocketInterceptor } from './features/shared/interceptors/web-socket.interceptor';
import { registerRuntimeStats } from './features/shared/utils/runtime-stats';
import { devTools } from './store/dev-tools';
import { RootStoreModule } from './store/root-store.module';

const importedDevTools = environment.production ? [] : [devTools];

const OVERLAY_CONFIG: OverlayDefaultConfig = {
  usePopover: false,
};

registerRuntimeStats();

if (environment.production) {
  enableProdMode();
}

bootstrapApplication(AppComponent, {
  providers: [
    importProvidersFrom(
      CommonModule,
      BrowserModule,
      NgSelectModule,
      MatIconModule,
      LoggingModule.forRoot({
        enableConsoleLog: !environment.production,
        logProviders: environment.production ? [RaygunLoggerService] : [ConsoleLoggerService],
        sensitiveActions: [],
      }),
      AuthModule.forRoot({
        domain: environment.auth0.domain,
        clientId: environment.auth0.clientId,
        authorizationParams: {
          // eslint-disable-next-line camelcase
          redirect_uri:
            typeof environment.auth0.loginRedirectUri === 'function'
              ? environment.auth0.loginRedirectUri()
              : environment.auth0.loginRedirectUri,
          scope: environment.auth0.scope,
          audience: environment.auth0.audience,
        },
        cacheLocation: 'localstorage',
        useRefreshTokens: true,
        skipRedirectCallback: window.location.pathname.includes('/auth/login/callback'),
      }),
      ...importedDevTools,
      RootStoreModule,
      StoreRouterConnectingModule.forRoot({
        navigationActionTiming: NavigationActionTiming.PostActivation,
      }),
      ServiceWorkerModule.register('safety-worker.js', {
        enabled: true,
        registrationStrategy: 'registerImmediately',
      })
    ),
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { subscriptSizing: 'dynamic' } },
    { provide: MAT_SLIDE_TOGGLE_DEFAULT_OPTIONS, useValue: { hideIcon: true, color: 'accent' } },
    {
      provide: MAT_BUTTON_TOGGLE_DEFAULT_OPTIONS,
      useValue: { hideSingleSelectionIndicator: true, hideMultipleSelectionIndicator: true },
    },
    provideZonelessChangeDetection(),
    provideRouter(appRoutes),
    { provide: HTTP_INTERCEPTORS, useClass: SessionSecurityInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: WebSocketInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ResolutionInterceptor, multi: true },
    provideHttpClient(withInterceptorsFromDi()),
    provideNoopAnimations(),
    { provide: OVERLAY_DEFAULT_CONFIG, useValue: OVERLAY_CONFIG },
  ],
}).catch(console.error);
